import {
  GameState,
  IsPlaying,
  Move,
  MoveType,
  NOCARD,
  PreviousTurn,
  RAFIKI_PENALTY,
  SlamObject,
  SlamState,
  TurnState,
} from "./constants";
import {isCorrectSlam, reportError, sum} from "./utils";
import { cardScore as scoreForRules } from "./redux/selectors";
import { presetOf } from "./common";
import { BotDifficulty } from "./redux/types";
import seedrandom from "seedrandom";
import { deck } from "./svgExports";
import isUndefined from "lodash/isUndefined";

export const UNKNOWN = "UNKNOWN";

// The average value of a card the bot can't see, under the room's house rules: 2 red Kings,
// 2 Jokers, 2 black Kings, Ace to 10 at face value (Ace 1) and the Jacks and Queens, i.e. 54 cards.
// (This used to be a fixed Rafiki-only figure that also counted the Kings twice.)
export const expectedCardValue = (rules?: string) => {
  const { values } = presetOf(rules);
  return (
    (2 * values.redKing +
      2 * values.blackKing +
      2 * values.joker +
      4 * values.jack +
      4 * values.queen +
      4 * ((10 * (10 + 1)) / 2)) / // Ace to 10
    54
  );
};

// The bots' source of randomness. Normally the browser's; in a seeded game (Daily Rafiki,
// challenge links) a generator seeded from the game and the position, so the same table
// plays out the same way for everyone (see getMove below).
let random: () => number = Math.random;

// This is to make sure that the bot doesnt always unknown cards from the same position
const randomizeExpectedValue = (e: number) => {
  return e + random() - 0.5;
};
const unknownCardValue = () => randomizeExpectedValue(expectedCardValue(activeRules));

// A random element. (Until 4 Nov 2026 this used Math.max instead of Math.min, so it always
// returned the last element: the "random" card was always the last player's.)
export const randomChoice = (arr: any[]) => {
  if (!arr.length) return undefined;
  const i = Math.min(Math.floor(random() * arr.length), arr.length - 1);
  return arr[i];
};

export interface BotGameState {
  topDiscard: string;
  playerRafiki: string;
  playerSlam?: string;
  slamOpponent?: string;
  drawnCard: string;
  correctSlamDone?: boolean;
  round: number;
  rounds: number;
  turn: number; // between 0 to playerIds.length - 1
  loop: number;
  lastTurn: number; // index of player id who plays last
  lastLoop: number; // index of player id who plays last
  turnState: TurnState;
  rules?: string; // house rules preset (common/index.ts); missing means Rafiki
  playerIds: string[]; // approved
  paused: boolean;
  isPlaying?: IsPlaying;
  slammedCards: {
    [key: string]: boolean;
  };
  previousTurn?: PreviousTurn;
  slam: SlamObject;
  lookingAtIndex?: number;
  lookingAtPlayer?: string;
  botMemory: {
    [key: string]: {
      [key: string]: Array<string>;
    };
  };
}

export interface BotMemory {
  [key: string]: string[];
}

const getBotGameState = (gameState: GameState, player: string): BotGameState => {
  return {
    topDiscard: gameState?.discardPile?.[gameState?.discardPile?.length - 1],
    playerRafiki: gameState?.playerRafiki,
    drawnCard: gameState?.drawnCard,
    correctSlamDone: gameState?.correctSlamDone,
    slammedCards: gameState?.slammedCards,
    round: gameState?.round,
    rounds: gameState?.rounds,
    turn: gameState?.turn,
    lastTurn: gameState?.lastTurn,
    lastLoop: gameState?.lastLoop,
    turnState: gameState?.turnState,
    rules: gameState?.rules,
    lookingAtIndex: gameState?.lookingAtIndex,
    lookingAtPlayer: gameState?.lookingAtPlayer,
    playerIds: gameState?.playerIds, // approved
    paused: gameState?.paused,
    isPlaying: gameState?.isPlaying,
    slam: gameState?.slam,
    previousTurn: gameState?.previousTurn,
    loop: gameState?.loop,
    botMemory: gameState?.botMemory,
  };
};

// House rules of the game the bot is currently playing: card values, whether a drawn card can be
// thrown away, and whose cards may be slammed all depend on the preset (see common/index.ts).
let activeRules: string | undefined;
const cardScore = (card: string) => scoreForRules(card, activeRules);
const activePreset = () => presetOf(activeRules);

const cardValue = (card: string) => {
  return card === UNKNOWN ? unknownCardValue() : cardScore(card);
};

// A card worth keeping whatever else happens: 0 points or fewer under the given house rules.
const isKeeper = (card: string, rules?: string) =>
  Boolean(card) && card !== UNKNOWN && card !== NOCARD && card in deck && scoreForRules(card, rules) <= 0;

export const getLargestCard = (hand: string[], self: string) => {
  let largestValue = -Infinity;
  let index = -1;

  hand.forEach((card, i) => {
    if (card === NOCARD) return;
    const value = cardValue(card);
    if (value > largestValue) {
      largestValue = value;
      index = i;
    }
  });
  if (index >= 0) {
    return {
      index,
      playerID: self,
    };
  }
  return;
};

const smallestOpponentCard = (memory: BotMemory, id: string, playerRafiki: string) => {
  let smallestValue = Infinity;
  let opponentId = "";
  let index = -1;

  Object.keys(memory)
    .filter((uid) => uid !== id)
    .forEach((uid) => {
      if (uid === playerRafiki) return;
      memory[uid].forEach((card, i) => {
        if (card === NOCARD) return;
        const value = cardValue(card);
        if (value <= smallestValue) {
          smallestValue = value;
          index = i;
          opponentId = uid;
        }
      });
    });
  if (index >= 0) {
    return {
      index,
      playerID: opponentId,
    };
  }
  return;
};

// The card a computer spies on (9 or 10). Until 4 Nov 2026 it was always the last unseen card in
// seating order, so a computer only ever spied on the player seated after it: if you sat first,
// no computer ever looked at your cards (player feedback, 4 Nov 2026). Now:
// - Rookie: any unseen opponent card, at random.
// - Voyager (half the time) and Master (most of the time): an unseen card of the opponent whose
//   hand looks lowest, i.e. the most dangerous one, whoever they are (people included).
// The Rafiki caller's cards can't be touched. With nothing unseen left, any card of theirs.
export const getUnseenOpponentCard = (
  memory: BotMemory,
  self: string,
  playerRafiki: string,
  difficulty: BotDifficulty = BotDifficulty.Master
) => {
  const opponents = Object.keys(memory).filter((uid) => uid !== self && uid !== playerRafiki);
  const unseen: { playerID: string; index: number }[] = [];
  const any: { playerID: string; index: number }[] = [];
  opponents.forEach((uid) =>
    memory[uid].forEach((card, i) => {
      if (card === NOCARD) return;
      any.push({ playerID: uid, index: i });
      if (card === UNKNOWN) unseen.push({ playerID: uid, index: i });
    })
  );
  if (!unseen.length) return randomChoice(any) as { playerID: string; index: number };
  const strategic =
    difficulty === BotDifficulty.Master ? 0.8 : difficulty === BotDifficulty.Voyager ? 0.5 : 0;
  if (random() < strategic) {
    const withUnseen = uniqueIds(unseen.map((c) => c.playerID));
    const handValue: { [uid: string]: number } = {};
    withUnseen.forEach((uid) => (handValue[uid] = getExpectedValueOfHand(memory[uid].filter((c) => c !== NOCARD))));
    const lowest = Math.min(...withUnseen.map((uid) => handValue[uid]));
    // Within a point of the lowest counts as just as dangerous: pick among them at random.
    const dangerous = withUnseen.filter((uid) => handValue[uid] - lowest < 1);
    const target = randomChoice(dangerous) as string | undefined;
    const pick = randomChoice(unseen.filter((c) => c.playerID === target));
    if (pick) return pick as { playerID: string; index: number };
  }
  return randomChoice(unseen) as { playerID: string; index: number };
};

const uniqueIds = (ids: string[]) => ids.filter((id, i) => ids.indexOf(id) === i);

const canSlamCard = (c: string, targetCard: string, botGameState: BotGameState) => {
  return c in deck && isCorrectSlam(c, targetCard) && cardScore(c) >= 0 && !isAlreadySlammed(botGameState, c);
};

const getSameValuedCardIndex = (hand: string[], drawnCard: string, botGameState: BotGameState) => {
  if (!drawnCard) return -1;
  return hand.findIndex((c) => canSlamCard(c, drawnCard, botGameState));
};

const getCardToSlam = (botGameState: BotGameState, hand: string[], topDiscard: string) => {
  if (!topDiscard) return;
  if (activePreset().slam === "none") return; // Cabo: no slamming
  // Dont slam red kings from own hand as their value is -1
  const index = hand.findIndex((c) => canSlamCard(c, topDiscard, botGameState));
  if (index >= 0) {
    return {
      index: index,
      value: hand[index],
    };
  }
};

const getOpponentCardToSlam = (botGameState: BotGameState, memory: BotMemory, topDiscard: string, self: string) => {
  const cards: any = [];
  if (!topDiscard) return;
  if (activePreset().slam !== "any") return; // Cambio sticks from your own hand only, Cabo not at all
  Object.keys(memory).forEach((uid) => {
    if (uid === self) return;

    memory[uid].forEach((card, i) => {
      if (isAlreadySlammed(botGameState, card)) {
        return;
      }
      if (botGameState?.playerRafiki === uid) {
        return;
      }
      if (isCorrectSlam(card, topDiscard)) {
        cards.push({
          index: i,
          value: card,
          playerID: uid,
        });
      }
    });
  });
  if (cards.length > 0) {
    return cards[0];
  }
};

const randomOpponentCard = (botGameState: BotGameState, memory: BotMemory, self: string) => {
  const cards: any = [];
  Object.keys(memory).forEach((uid) => {
    if (uid === self) return;
    memory[uid].forEach((card, i) => {
      if (isAlreadySlammed(botGameState, card)) return;
      if (card === NOCARD) return;
      cards.push({
        index: i,
        value: card,
        playerID: uid,
      });
    });
  });
  return randomChoice(cards);
};

const randomOwnCard = (botGameState: BotGameState, memory: BotMemory, self: string) => {
  const cards: any = [];
  Object.keys(memory).forEach((uid) => {
    if (uid !== self) return;
    memory[uid].forEach((card, i) => {
      if (isAlreadySlammed(botGameState, card)) return;
      if (card === NOCARD) return;
      cards.push({
        index: i,
        value: card,
        playerID: uid,
      });
    });
  });
  return randomChoice(cards);
};

const getExpectedValueOfHand = (hand: string[]) => {
  return sum(
    hand.map((card) => {
      if (card === UNKNOWN) {
        return unknownCardValue();
      }
      return cardScore(card);
    })
  );
};

const isEmpty = (hand: string[]) => {
  return hand.every(card => card === NOCARD);
};

const shouldCallRafiki = (memory: BotMemory, self: string, loop: number) => {
  const ownExpectedValue = getExpectedValueOfHand(memory[self]);
  const opponents = Object.keys(memory).filter((x) => x !== self);

  const opponentExpectedValues = opponents.map((uid) => {
    return getExpectedValueOfHand(memory[uid]);
  });

  if (loop < 3) {
    return false;
  }
  // Calling wrongly costs 10 points, so the computer wants a clear lead, but it shouldn't sit on
  // a strong hand for ages either.
  // More opponents means more chance one of them is secretly lower, so ask for a bigger lead.
  const extra = Math.max(0, opponents.length - 1);
  if (ownExpectedValue <= 3) {
    return opponentExpectedValues.every((x) => x >= ownExpectedValue + 2 + extra);
  }
  if (ownExpectedValue <= 7) {
    return opponentExpectedValues.every((x) => x >= ownExpectedValue + 4 + extra);
  }
  if (loop >= 6 && ownExpectedValue <= 10) {
    return opponentExpectedValues.every((x) => x >= ownExpectedValue + 6 + extra);
  }
  return false;
};

const isAlreadySlammed = (botGameState: BotGameState, card: string) => {
  return card in botGameState?.slammedCards;
};

export class Bot {
  private readonly self: string;
  readonly difficulty: BotDifficulty;

  constructor(botId: string, difficulty: BotDifficulty) {
    this.self = botId;
    this.difficulty = difficulty;
  }
  getMemory = (botGameState: BotGameState) => {
    const memory = botGameState.botMemory[this.self];
    if (this.difficulty === BotDifficulty.Master) {
      return memory;
    }
    // This is to make sure that the bot random behavior is predictable and depends on the bot id
    // We dont need to store faded memories in the db and can calculate them on the fly
    const rng = seedrandom(this.self);
    const result: BotMemory = {};
    const probabilityOfRemembering = {
      [BotDifficulty.Master]: 1,
      [BotDifficulty.Voyager]: 0.5,
      [BotDifficulty.Rookie]: 0.2,
    };

    for (const key of botGameState?.playerIds) {
      result[key] = [];
      for (let i = 0; i < memory[key].length; i++) {
        const random = rng();
        // A bot never forgets a keeper in its own hand: a card worth 0 or less under the room's
        // rules (red King and Joker in Rafiki, red King -3 in Gringo...). Forgetting it made
        // Rookie and Voyager bots treat it as an average unseen card and replace it or give it
        // away in a swap, often the very red King they had just chosen to keep.
        const ownKeeper = key === this.self && isKeeper(memory[key][i], botGameState?.rules);
        if (random >= probabilityOfRemembering[this.difficulty] && memory[key][i] !== NOCARD && !ownKeeper) {
          result[key][i] = UNKNOWN;
        } else {
          result[key][i] = memory[key][i];
        }
      }
    }
    return result;
  };
  memoryWithOnlyActivePlayers = (botGameState: any) => {
    const memory = this.getMemory(botGameState);
    const result: BotMemory = {};
    Object.keys(memory).forEach((playerId) => {
      if (botGameState?.isPlaying?.[playerId]) {
        result[playerId] = memory?.[playerId];
      }
    });
    return result;
  };

  public getMove = (gameState: GameState): Move | undefined => {
    const self = this.self;
    activeRules = gameState?.rules;

    const botGameState = getBotGameState(gameState, self);
    const memory = this.memoryWithOnlyActivePlayers(botGameState);

    botGameState.playerIds.forEach((id) => {
      gameState?.hands?.[id].forEach((card, i) => {
        if (!(id in memory)) {
          memory[id] = [];
        }
        if (!memory[id][i]) {
          memory[id][i] = UNKNOWN;
        }
        if (card === NOCARD) {
          memory[id][i] = NOCARD;
        }
      });
    });

    const drawn = botGameState?.drawnCard;
    const topDiscard = botGameState?.topDiscard;
    const myHand = memory[self];
    const cardToSlam = getCardToSlam(botGameState, myHand, topDiscard);
    const opponentCardToSlam = getOpponentCardToSlam(botGameState, memory, topDiscard, self);
    const randomOwn = randomOwnCard(botGameState, memory, self);
    const randomOpponent = randomOpponentCard(botGameState, memory, self);
    const myRafiki = botGameState.playerRafiki === self;

    const largest = getLargestCard(myHand, self);

    if (
      botGameState?.loop >= botGameState?.lastLoop &&
      botGameState?.turn >= botGameState?.lastTurn &&
      botGameState?.lastTurn >= 0
    ) {
      return;
    }

    if (botGameState?.turnState === TurnState.Drawn) {
      return {
        type: MoveType.Discard,
        self: self,
      };
    }

    // Prefer opponent slam because we get to choose what card to discard
    if (opponentCardToSlam) {
      if (botGameState?.slam?.slamState === SlamState.NoSlam && !botGameState?.drawnCard && !myRafiki) {
        return {
          type: MoveType.Slam,
          self,
        };
      }
      if (botGameState?.slam?.player === self) {
        if (botGameState?.slam?.slamState === SlamState.SlamStart) {
          return {
            type: MoveType.PickSlamCard,
            self,
            playerIndex: opponentCardToSlam.index,
            player: opponentCardToSlam.playerID,
          };
        }
      }
    }

    if (botGameState?.slam?.player === self && largest) {
      if (botGameState?.slam?.slamState === SlamState.SlamSuccessOpponent) {
        return {
          type: MoveType.FinishCorrectSlamOpponent,
          self,
          selfIndex: largest.index,
        };
      }
    }

    if (cardToSlam) {
      if (botGameState?.slam?.slamState === SlamState.NoSlam && !botGameState?.drawnCard && !myRafiki) {
        return {
          type: MoveType.Slam,
          self,
        };
      }
      if (botGameState?.slam?.player === self) {
        if (botGameState?.slam?.slamState === SlamState.SlamStart) {
          return {
            type: MoveType.PickSlamCard,
            self,
            playerIndex: cardToSlam.index,
            player: this.self,
          };
        }
      }
    }

    if (
      (botGameState?.slam?.slamState === SlamState.SlamFailureOwn ||
        botGameState?.slam?.slamState === SlamState.SlamFailureOpponent) &&
      botGameState?.slam?.player === self &&
      randomOwn
    ) {
      return {
        type: MoveType.FinishIncorrectSlam,
        self,
      };
    }

    // Backup slam random card
    if (botGameState?.slam?.slamState === SlamState.SlamStart && botGameState?.slam?.player === self && randomOwn) {
      return {
        type: MoveType.PickSlamCard,
        self,
        playerIndex: randomOwn.index,
        player: this.self,
      };
    }
    if (
      botGameState?.slam?.slamState === SlamState.SlamStart &&
      botGameState?.slam?.player === self &&
      randomOpponent
    ) {
      return {
        type: MoveType.PickSlamCard,
        self,
        playerIndex: randomOpponent.index,
        player: this.self,
      };
    }

    if (self !== botGameState?.playerIds?.[botGameState?.turn]) {
      return; // Not my turn
    }

    if (botGameState?.turnState === TurnState.Draw) {
      if (isEmpty(myHand)) {
        // No cards left: call RAFIKI, or if someone already has, just pass (drawing with an
        // empty hand skips the turn).
        return {
          type: botGameState?.playerRafiki ? MoveType.Draw : MoveType.CallRafiki,
          self: self,
        };
      }
      if (shouldCallRafiki(memory, self, botGameState.loop) && !botGameState?.playerRafiki && botGameState.loop >= 3) {
        return {
          type: MoveType.CallRafiki,
          self: self,
        };
      }

      return {
        type: MoveType.Draw,
        self: self,
      };
    }

    if (botGameState?.turnState === TurnState.Replace) {
      const index = activePreset().slam === "none" ? -1 : getSameValuedCardIndex(myHand, drawn, botGameState);
      if (index >= 0) {
        // Replace then slam
        return {
          type: MoveType.Replace,
          self: self,
          selfIndex: index,
        };
      }
      // Gringo, Cambio, Cabo, Kaboo: a card no better than our worst one goes straight on the discard pile
      if (
        activePreset().discardAnyCard &&
        drawn &&
        (!largest || cardScore(drawn) >= cardValue(myHand[largest.index]))
      ) {
        return {
          type: MoveType.Discard,
          self: self,
        };
      }
      if (largest) {
        // Replace
        return {
          type: MoveType.Replace,
          self: self,
          selfIndex: largest.index,
        };
      }
    }

    if (botGameState?.turnState === TurnState.LookAtOwn) {
      // Look at unseen card
      let index = myHand.indexOf(UNKNOWN);
      if (index === -1) {
        index = 0;
      }
      return {
        type: MoveType.StartLookAtOwn,
        self: self,
        selfIndex: index,
      };
    }

    if (botGameState?.turnState === TurnState.LookingAtOwn && !isUndefined(botGameState?.lookingAtIndex)) {
      return {
        type: MoveType.FinishLookAtOwn,
        self: self,
        selfIndex: botGameState?.lookingAtIndex,
      };
    }

    if (botGameState?.turnState === TurnState.LookAtOpponents) {
      // Look at unseen card
      const card = getUnseenOpponentCard(memory, this.self, botGameState.playerRafiki, this.difficulty);
      return {
        type: MoveType.StartLookAtOpponents,
        self: self,
        opponentIndex: card.index,
        opponent: card.playerID,
      };
    }

    if (
      botGameState?.turnState === TurnState.LookingAtOpponents &&
      !isUndefined(botGameState?.lookingAtPlayer) &&
      !isUndefined(botGameState?.lookingAtIndex)
    ) {
      return {
        type: MoveType.FinishLookAtOpponents,
        self: self,
        opponentIndex: botGameState?.lookingAtIndex,
        opponent: botGameState?.lookingAtPlayer,
      };
    }

    if (botGameState?.turnState === TurnState.SwapWithOpponent && largest) {
      // Swap (largest, smallest)
      // Rookies mostly swap with any opponent's card at random; Voyager and Master take the
      // lowest card they know of (or think likely), from whoever holds it, people included.
      const rookieRandom = this.difficulty === BotDifficulty.Rookie && random() < 0.6;
      const smallest = rookieRandom
        ? (randomChoice(
            Object.keys(memory)
              .filter((uid) => uid !== self && uid !== botGameState.playerRafiki)
              .flatMap((uid) =>
                memory[uid].map((card, index) => ({ card, index, playerID: uid })).filter((c) => c.card !== NOCARD)
              )
              .map(({ index, playerID }) => ({ index, playerID }))
          ) as { index: number; playerID: string } | undefined) || smallestOpponentCard(memory, self, botGameState.playerRafiki)
        : smallestOpponentCard(memory, self, botGameState.playerRafiki);
      if (smallest) {
        return {
          type: MoveType.SwapWithOpponent,
          self: self,
          selfIndex: largest.index,
          opponent: smallest.playerID,
          opponentIndex: smallest.index,
        };
      }
    }

    return;
  };
}

let bot: Bot;

const bots: any = {};

export const getMove = (botId: string, difficulty: BotDifficulty, gameState: GameState) => {
  // A new bot when the difficulty changes too (the Masters ladder raises it between games).
  if (!(botId in bots) || bots[botId].difficulty !== difficulty) {
    bot = new Bot(botId, difficulty);
    bots[botId] = bot;
  }
  if (gameState?.seed) {
    random = seedrandom(`${gameState.seed}:${botId}:${gameState.loop}:${gameState.turn}:${gameState.turnState}`);
  }
  try {
    return bots?.[botId]?.getMove(gameState);
  } finally {
    random = Math.random;
  }
};

// The computers dealt into this round: only they have a memory. A round of death deals in just the
// tied players, so a computer that wasn't tied sits it out with no memory at all. Updating every
// computer in the room threw on the first card put down in a round of death, so every move in it
// was turned down and the table froze (player feedback, 4 Nov 2026).
export const botsInRound = (gameState: GameState): string[] =>
  Object.keys(gameState?.bots || {}).filter((botId) => Boolean(gameState?.botMemory?.[botId]));

// One card in one computer's memory of one player's hand. Nothing to do for a player it holds no
// memory of (someone not dealt into this round).
const remember = (gameState: GameState, botId: string, player: string, index: number, card: any) => {
  const hand = gameState?.botMemory?.[botId]?.[player];
  if (!hand || typeof index !== "number" || index < 0) return;
  hand[index] = card;
};

const validateBotMemories = (gameState: GameState, move: Move) => {
  const botIds = botsInRound(gameState);
  botIds.forEach((botId: string) => {
    gameState?.playerIds.forEach((player: string) => {
      gameState?.botMemory?.[botId]?.[player]?.forEach((memcard: string, i: number) => {
        const card = gameState?.hands?.[player]?.[i];
        if (memcard === UNKNOWN && card !== NOCARD) {
          return;
        }
        if (memcard !== card) {
          const e = new Error(`Bot memory mismatch ${memcard} ${card} ${botId} ${i} ${JSON.stringify(move, null, 4)}`);
          reportError(e);
        }
      });
    });
  });
};

export const updateAllMemoriesForBots = (gameState: GameState, move: Move) => {
  const newGameState = { ...gameState };
  const botIds = botsInRound(gameState);
  if (move.type === MoveType.PickSlamCard) {
    const slammedCardIndex = move.playerIndex;
    const opponent = move.player;
    const self = move.self;
    botIds.forEach((botId: string) => {
      const card = newGameState?.botMemory?.[botId]?.[opponent]?.[slammedCardIndex];
      remember(newGameState, botId, opponent, slammedCardIndex, card);
    });

    if (
      gameState?.slam?.slamState === SlamState.SlamFinishSuccessOwn ||
      gameState?.slam?.slamState === SlamState.SlamSuccessOpponent
    ) {
      botIds.forEach((botId: string) => {
        remember(newGameState, botId, opponent, slammedCardIndex, NOCARD);
      });
    }
  }

  if (
    move.type === MoveType.FinishCorrectSlamOpponent &&
    newGameState?.slam?.slamState === SlamState.SlamFinishSuccessOpponent
  ) {
    const slamPlayer = move.self;
    const slammedCardIndex = move.selfIndex;
    const opponent = newGameState?.slam?.opponent;
    const discardIndex = newGameState?.slam?.discardIndex;
    const receiveIndex = newGameState?.slam?.receiveIndex;

    botIds.forEach((botId: string) => {
      const card = newGameState?.botMemory?.[botId]?.[slamPlayer]?.[discardIndex];

      remember(newGameState, botId, opponent, receiveIndex, card);
      remember(newGameState, botId, slamPlayer, discardIndex, NOCARD);
    });
  }

  if (move.type === MoveType.FinishIncorrectSlam) {
    const slamPlayer = move.self;
    // Every slot that now holds a penalty card (none if the hand was full, two in Kaboo).
    const hand: string[] = newGameState?.hands?.[slamPlayer] || [];
    botIds.forEach((botId: string) => {
      const memory = newGameState?.botMemory?.[botId]?.[slamPlayer];
      hand.forEach((card, index) => {
        if (card !== NOCARD && memory?.[index] === NOCARD) remember(newGameState, botId, slamPlayer, index, UNKNOWN);
      });
    });
  }

  if (move.type === MoveType.Replace) {
    const player = move?.self;
    const selfIndex = move?.selfIndex;
    const card = gameState?.hands?.[player]?.[selfIndex];
    botIds.forEach((botId: string) => {
      if (player !== botId) {
        remember(newGameState, botId, player, selfIndex, UNKNOWN);
      } else {
        remember(newGameState, botId, botId, selfIndex, card);
      }
    });
  }
  if (move.type === MoveType.FinishLookAtOwn) {
    const player = move?.self;
    const selfIndex = move?.selfIndex;
    const card = gameState?.hands?.[player]?.[selfIndex];
    botIds.forEach((botId: string) => {
      if (player !== botId) {
        return;
      }
      remember(newGameState, botId, botId, selfIndex, card);
    });
  }
  if (move.type === MoveType.FinishLookAtOpponents) {
    const player = move?.self;
    const opponent = move?.opponent;
    const opponentIndex = move?.opponentIndex;
    const card = gameState?.hands?.[opponent]?.[opponentIndex];
    botIds.forEach((botId: string) => {
      if (player !== botId) {
        return;
      }
      remember(newGameState, botId, opponent, opponentIndex, card);
    });
  }
  if (move.type === MoveType.SwapWithOpponent) {
    const player = move?.self;
    const opponent = move?.opponent;
    const opponentIndex = move?.opponentIndex;
    const playerIndex = move?.selfIndex;
    botIds.forEach((botId: string) => {
      const opponentCard = newGameState?.botMemory?.[botId]?.[opponent]?.[opponentIndex];
      const myCard = newGameState?.botMemory?.[botId]?.[player]?.[playerIndex];
      remember(newGameState, botId, player, playerIndex, opponentCard);
      remember(newGameState, botId, opponent, opponentIndex, myCard);
    });
  }

  validateBotMemories(newGameState, move);
  return newGameState;
};
